import type { BlockType } from '@unifyapps/defs/types/block';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import { useDataSourceRecordStore } from '../../../stores/DataSourceRecordStore';
import DataSourceHelper from '../../../helper/DataSourceHelper';
import { useGlobalStateStore } from '../../../stores/GlobalStateStore';
import DependencyFlowHelper from '../../../stores/GlobalStateStore/helpers/DependencyFlowHelper';
import { ComputeContextHelper } from '../../../hooks/computeContext/ComputeContextHelper';
import { DependencyPaths } from '../../../helper/DependencyPaths';
import { ComputeContextKeys } from '../../../hooks/computeContext/const';
import { DependencyHelper } from '../../../../dependency/helpers/DependencyHelper';
import type { ComputeContextType } from '../../../hooks/computeContext/BuildComputeContext';

export function getIsEntitySuspendedDependencies(dataSourceIds: string[]) {
  const dsDep = dataSourceIds.map<DependsOnType>((dsId) => {
    const paths = DependencyPaths.dataSource(dsId);
    return {
      id: dsId,
      p: [paths.isLoading],
    };
  });

  const sessionStoragePath = DependencyPaths.sessionStorage();

  const sessionStorage: DependsOnType[] = [
    {
      id: ComputeContextKeys.SESSION_STORAGE,
      p: [sessionStoragePath.isLoading],
    },
  ];

  return DependencyHelper.mergeDependsOn([sessionStorage, dsDep]);
}

export function useIsEntitySuspended({
  dataSourceIds = [],
  context,
}: {
  dataSourceIds: BlockType['dataSourceIds'];
  context: ComputeContextType;
}) {
  // todo: need to remove useGlobalStateStore
  const dsDependencyFlow = useGlobalStateStore().use.dependencyFlow.dataSources();
  const dataSourceRecords = useDataSourceRecordStore().use.dataSources();

  const automaticDataSourceIds = useMemo(() => {
    // it is possible that we might have includes manual data source id in dataSourceIds
    return dataSourceIds.filter((id) => {
      return !DataSourceHelper.isManualDataSource(dataSourceRecords[id]);
    });
  }, [dataSourceIds, dataSourceRecords]);

  const areAllQueriesReady = automaticDataSourceIds.every((id) => {
    return DependencyFlowHelper.isDependencyFinished(dsDependencyFlow, id);
  });

  const isWaitingForRequestsToBeMade = automaticDataSourceIds.some((id) => {
    const dataSourceQueryResult = ComputeContextHelper.getDataSourceQueryResult(context, id);
    return isEmpty(dataSourceQueryResult);
  });

  const isLoadingBlockingQueries = automaticDataSourceIds.some((id) => {
    const dataSourceQueryResult = ComputeContextHelper.getDataSourceQueryResult(context, id);
    return dataSourceQueryResult?.isLoading !== false;
  });

  return {
    isSuspended: isLoadingBlockingQueries || isWaitingForRequestsToBeMade || !areAllQueriesReady,
  };
}
